import { useEffect, useState, useCallback, createContext, useContext, useRef } from 'react'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { ListNode, ListItemNode } from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getRoot, $createParagraphNode, $insertNodes, $getNodeByKey } from 'lexical'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import {
  FORMAT_TEXT_COMMAND,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
} from 'lexical'
import { $createHeadingNode } from '@lexical/rich-text'
import { INSERT_UNORDERED_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND } from '@lexical/list'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Bold, Italic, List, ListOrdered, Heading1, Heading2, Heading3, MousePointerClick, AlignLeft, AlignCenter, AlignRight } from 'lucide-react'
import { CTAButtonNode, $createCTAButtonNode, $isCTAButtonNode, type CTAButtonAlignment } from './CTAButtonNode'

import type { LexicalEditor, NodeKey } from 'lexical'

// Context for CTA button editing
interface CTAModalContextType {
  openModal: (nodeKey?: NodeKey, initialText?: string, initialUrl?: string, initialAlign?: CTAButtonAlignment) => void
}

const CTAModalContext = createContext<CTAModalContextType | null>(null)

function useCTAModal() {
  const context = useContext(CTAModalContext)
  if (!context) {
    throw new Error('useCTAModal must be used within CTAModalContext')
  }
  return context
}

interface SimpleLexicalEditorProps {
  onChange: (html: string) => void
  initialValue?: string
  placeholder?: string
  onEditorReady?: (editor: LexicalEditor) => void
}

const editorTheme = {
  heading: {
    h1: 'text-3xl font-bold mb-4',
    h2: 'text-2xl font-bold mb-3',
    h3: 'text-xl font-bold mb-2',
  },
  list: {
    ul: 'list-disc ml-5 mb-2',
    ol: 'list-decimal ml-5 mb-2',
    listitem: 'mb-1',
  },
  text: {
    bold: 'font-bold',
    italic: 'italic',
  },
  paragraph: 'mb-2',
}

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext()
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const ctaModal = useCTAModal()

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          setIsBold(selection.hasFormat('bold'))
          setIsItalic(selection.hasFormat('italic'))
        }
        return false
      },
      COMMAND_PRIORITY_LOW
    )
  }, [editor])

  const formatText = (format: 'bold' | 'italic') => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)
  }

  const formatHeading = (headingType: 'h1' | 'h2' | 'h3' | 'paragraph') => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        if (headingType === 'paragraph') {
          const paragraph = $createParagraphNode()
          selection.insertNodes([paragraph])
        } else {
          const heading = $createHeadingNode(headingType)
          selection.insertNodes([heading])
        }
      }
    })
  }

  const formatList = (listType: 'ul' | 'ol') => {
    if (listType === 'ul') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    }
  }

  const handleInsertCTA = () => {
    ctaModal.openModal()
  }

  return (
    <div className="flex flex-wrap gap-1 border-b border-gray-200 bg-gray-50 p-2">
      <Button
        type="button"
        variant={isBold ? 'default' : 'outline'}
        size="sm"
        onClick={() => formatText('bold')}
        className="h-8 w-8 p-0"
        title="Bold"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={isItalic ? 'default' : 'outline'}
        size="sm"
        onClick={() => formatText('italic')}
        className="h-8 w-8 p-0"
        title="Italic"
      >
        <Italic className="h-4 w-4" />
      </Button>

      <div className="mx-2 h-8 w-px bg-gray-300" />

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => formatHeading('h1')}
        className="h-8 w-8 p-0"
        title="Heading 1"
      >
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => formatHeading('h2')}
        className="h-8 w-8 p-0"
        title="Heading 2"
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => formatHeading('h3')}
        className="h-8 w-8 p-0"
        title="Heading 3"
      >
        <Heading3 className="h-4 w-4" />
      </Button>

      <div className="mx-2 h-8 w-px bg-gray-300" />

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => formatList('ul')}
        className="h-8 w-8 p-0"
        title="Bullet List"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => formatList('ol')}
        className="h-8 w-8 p-0"
        title="Numbered List"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>

      <div className="mx-2 h-8 w-px bg-gray-300" />

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleInsertCTA}
        className="h-8 px-2"
        title="Insert CTA Button"
      >
        <MousePointerClick className="mr-1 h-4 w-4" />
        <span className="text-xs">CTA</span>
      </Button>
    </div>
  )
}

function OnChangePlugin({ onChange }: { onChange: (html: string) => void }) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor)
        onChange(html)
      })
    })
  }, [editor, onChange])

  return null
}

function EditorReadyPlugin({ onEditorReady }: { onEditorReady?: (editor: LexicalEditor) => void }) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    if (onEditorReady) {
      onEditorReady(editor)
    }
  }, [editor, onEditorReady])

  return null
}

function InitialValuePlugin({ initialValue, onChange }: { initialValue?: string; onChange: (html: string) => void }) {
  const [editor] = useLexicalComposerContext()
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (hasInitialized.current || !initialValue || !initialValue.trim()) {
      return
    }
    
    hasInitialized.current = true

    // Use queueMicrotask to ensure editor is fully ready
    queueMicrotask(() => {
      editor.update(() => {
        const root = $getRoot()
        root.clear()

        // Parse HTML content
        const parser = new DOMParser()
        const dom = parser.parseFromString(initialValue, 'text/html')
        
        let nodes: ReturnType<typeof $generateNodesFromDOM> = []
        try {
          nodes = $generateNodesFromDOM(editor, dom)
        } catch (err) {
          console.error('Error generating nodes from DOM:', err)
        }

        // Filter and append valid nodes
        const validNodes = nodes.filter(node => node != null)
        
        for (const node of validNodes) {
          try {
            root.append(node)
          } catch (err) {
            console.error('Error appending node:', err, node)
          }
        }
        
        // Ensure there's at least one child
        if (root.getChildrenSize() === 0) {
          root.append($createParagraphNode())
        }
      })

      // Update parent with normalized HTML after a short delay
      setTimeout(() => {
        try {
          const html = editor.getEditorState().read(() => $generateHtmlFromNodes(editor))
          onChange(html)
        } catch (err) {
          console.error('Error generating HTML:', err)
        }
      }, 50)
    })
  }, [editor, initialValue, onChange])

  return null
}

// CTA Button Modal Component
interface CTAModalState {
  isOpen: boolean
  editingNodeKey: NodeKey | null
  text: string
  url: string
  align: CTAButtonAlignment
}

function CTAButtonModal({
  state,
  onClose,
  onSave,
}: {
  state: CTAModalState
  onClose: () => void
  onSave: (text: string, url: string, align: CTAButtonAlignment) => void
}) {
  const [text, setText] = useState(state.text)
  const [url, setUrl] = useState(state.url)
  const [align, setAlign] = useState<CTAButtonAlignment>(state.align)

  useEffect(() => {
    setText(state.text)
    setUrl(state.url)
    setAlign(state.align)
  }, [state.text, state.url, state.align, state.isOpen])

  const handleSave = () => {
    if (!text.trim() || !url.trim()) return
    onSave(text.trim(), url.trim(), align)
    onClose()
  }

  const isEditing = state.editingNodeKey !== null

  const alignmentClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[align]

  return (
    <Dialog open={state.isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit CTA Button' : 'Insert CTA Button'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the button text, link URL, and alignment.'
              : 'Add a call-to-action button to your email.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="cta-text">Button Text</Label>
            <Input
              id="cta-text"
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="e.g., Get Started"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cta-url">Button URL</Label>
            <Input
              id="cta-url"
              value={url}
              onChange={e => setUrl(e.target.value)}
              placeholder="e.g., https://platform.cobrief.app/login"
            />
          </div>
          <div className="space-y-2">
            <Label>Alignment</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={align === 'left' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAlign('left')}
                className="flex-1"
              >
                <AlignLeft className="mr-2 h-4 w-4" />
                Left
              </Button>
              <Button
                type="button"
                variant={align === 'center' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAlign('center')}
                className="flex-1"
              >
                <AlignCenter className="mr-2 h-4 w-4" />
                Center
              </Button>
              <Button
                type="button"
                variant={align === 'right' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAlign('right')}
                className="flex-1"
              >
                <AlignRight className="mr-2 h-4 w-4" />
                Right
              </Button>
            </div>
          </div>
          {/* Preview */}
          {text && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className={`rounded-lg border bg-muted/30 p-4 ${alignmentClass}`}>
                <span
                  className="inline-block rounded-md px-6 py-3 font-semibold text-black"
                  style={{ backgroundColor: '#c6f648' }}
                >
                  {text}
                </span>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={!text.trim() || !url.trim()}>
            {isEditing ? 'Update' : 'Insert'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Plugin to handle clicks on CTA buttons for editing
function CTAClickHandlerPlugin() {
  const [editor] = useLexicalComposerContext()
  const ctaModal = useCTAModal()

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      const ctaButton = target.closest('.cta-button-inner')
      
      if (ctaButton) {
        const ctaWrapper = ctaButton.closest('.cta-button-wrapper')
        const nodeKey = ctaWrapper?.getAttribute('data-node-key')
        if (nodeKey) {
          editor.getEditorState().read(() => {
            const node = $getNodeByKey(nodeKey)
            if ($isCTAButtonNode(node)) {
              ctaModal.openModal(nodeKey, node.getText(), node.getUrl(), node.getAlign())
            }
          })
        }
      }
    }

    const rootElement = editor.getRootElement()
    if (rootElement) {
      rootElement.addEventListener('click', handleClick)
      return () => rootElement.removeEventListener('click', handleClick)
    }
  }, [editor, ctaModal])

  return null
}

export default function SimpleLexicalEditor({
  onChange,
  initialValue,
  placeholder = 'Enter your message here...',
  onEditorReady,
}: SimpleLexicalEditorProps) {
  const [modalState, setModalState] = useState<CTAModalState>({
    isOpen: false,
    editingNodeKey: null,
    text: '',
    url: '',
    align: 'left',
  })
  const [editor, setEditor] = useState<LexicalEditor | null>(null)

  const openModal = useCallback((nodeKey?: NodeKey, initialText?: string, initialUrl?: string, initialAlign?: CTAButtonAlignment) => {
    setModalState({
      isOpen: true,
      editingNodeKey: nodeKey || null,
      text: initialText || '',
      url: initialUrl || '',
      align: initialAlign || 'left',
    })
  }, [])

  const closeModal = useCallback(() => {
    setModalState(prev => ({ ...prev, isOpen: false }))
  }, [])

  const handleSave = useCallback(
    (text: string, url: string, align: CTAButtonAlignment) => {
      if (!editor) return

      editor.update(() => {
        if (modalState.editingNodeKey) {
          // Editing existing node
          const node = $getNodeByKey(modalState.editingNodeKey)
          if ($isCTAButtonNode(node)) {
            node.setText(text)
            node.setUrl(url)
            node.setAlign(align)
          }
        } else {
          // Inserting new node
          const ctaNode = $createCTAButtonNode({ text, url, align })
          $insertNodes([ctaNode])
        }
      })
    },
    [editor, modalState.editingNodeKey]
  )

  const ctaModalContextValue = { openModal }

  const initialConfig = {
    namespace: 'SimpleLexicalEditor',
    theme: editorTheme,
    onError: (error: Error) => {
      console.error('Editor error:', error)
    },
    nodes: [HeadingNode, ListNode, ListItemNode, QuoteNode, CTAButtonNode],
  }

  return (
    <CTAModalContext.Provider value={ctaModalContextValue}>
      <div className="rounded-lg border">
        <LexicalComposer initialConfig={initialConfig}>
          <ToolbarPlugin />
          <div className="relative">
            <RichTextPlugin
              contentEditable={<ContentEditable className="min-h-[300px] resize-none p-4 text-sm outline-none" />}
              placeholder={
                <div className="pointer-events-none absolute left-4 top-4 select-none text-sm text-muted-foreground">
                  {placeholder}
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary as any}
            />
            <HistoryPlugin />
            <AutoFocusPlugin />
            <ListPlugin />
            <OnChangePlugin onChange={onChange} />
            <InitialValuePlugin initialValue={initialValue} onChange={onChange} />
            <EditorReadyPlugin
              onEditorReady={editorInstance => {
                setEditor(editorInstance)
                onEditorReady?.(editorInstance)
              }}
            />
            <CTAClickHandlerPlugin />
          </div>
        </LexicalComposer>
        <CTAButtonModal state={modalState} onClose={closeModal} onSave={handleSave} />
      </div>
    </CTAModalContext.Provider>
  )
}
